

import React, { useContext, useEffect, useState } from 'react';
import { WhiteContainer, RadioButtons, Actions, ContentContainer } from '../../../../../styles/orderform.tsx';
import { apartmentSizes, apartmentTypes, preferences } from '../../data.ts';
import { OrderContext } from '../../OrderContext.tsx';
import { ApartmentSize, Order, ValidationErrors } from '../../types.ts';
import ErrorWrapper from '../Common/ErrorWrapper.tsx';
import { useTranslation } from 'react-i18next';

function PhaseOne() {

  const { formValues, handleFormChange, toPhase } = useContext(OrderContext);
  const { t } = useTranslation();

  const [sizes, setSizes] = useState(apartmentSizes['kerrostalo']);
  const [showError, setShowError] = useState(false);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});

  const validateFirstPhase = () => {
    setFormErrors({});
    // Check that all required fields are filled
    const validationErrors: ValidationErrors = {};
    if (!formValues['customer-type']) {
      validationErrors['customer-type'] = 'required';
    }
    if (!formValues['apartment-type']) {
      validationErrors['apartment-type'] = 'required';
    }
    if (!formValues['apartment-size']) {
      validationErrors['apartment-size'] = 'required';
    }
    if (!formValues['consumption-estimate']) {
      validationErrors['consumption-estimate'] = 'required';
    }
    if (!formValues['preferences']) {
      validationErrors['preferences'] = 'required';
    }
    setFormErrors(validationErrors);
    return validationErrors;
  }

  const phaseOneFields = ['customer-type', 'apartment-type', 'apartment-size', 'consumption-estimate', 'preferences'];
  useEffect(() => {
    // Validate first phase when any of the fields change
    validateFirstPhase();
  }, [...phaseOneFields.map(field => formValues[field as keyof Order])]);

  useEffect(() => {
    const types = apartmentTypes.map((option) => option.value);
    const type = formValues['apartment-type'] === '' ? 'kerrostalo' : types.includes(formValues['apartment-type']) ? formValues['apartment-type'] : 'kerrostalo'
    const sizes = apartmentSizes[type];
    setSizes(sizes);
  }, [formValues['apartment-type']]);

  const toNextPhase = () => {
    const validationErrors = validateFirstPhase();
    if (Object.keys(validationErrors).length > 0) {
      setShowError(true);
      setFormErrors(validationErrors);
      return;
    }
    toPhase(2);
  }

  const formChangeHandler = (name: string, value: string) => {
    const event = { target: { name, value } } as React.ChangeEvent<HTMLInputElement>;
    handleFormChange(event);
  }

  return (
    <>
      {/* Asumismuoto */}
      <ErrorWrapper showError={showError && !formValues["apartment-type"]}>
        <WhiteContainer id='apartment-type' className={showError && !formValues["apartment-type"] ? 'error' : ''}>
          <h2>{t('phaseOne.chooseApartmentType')}</h2>
          <RadioButtons>
            {apartmentTypes.map((option) => (
              <label className='radio-label' htmlFor={option.value} key={option.value}>
                <input type="radio"
                  name="apartment-type"
                  value={option.value}
                  id={option.value}
                  checked={formValues['apartment-type'] === option.value}
                  onChange={handleFormChange} />
                <div className='image-container'>
                  <img src={option.img} alt={option.imgAlt} />
                </div>
                {t(option.label)}
              </label>
            ))}
          </RadioButtons>
        </WhiteContainer>
      </ErrorWrapper >

      {/* Asunnon koko */}
      <ErrorWrapper showError={showError && !formValues["apartment-size"]
      }>
        <WhiteContainer id='apartment-size' className={showError && !formValues["apartment-size"] ? 'error' : ''}>
          <h2>{t('phaseOne.apartmentSize')}</h2>
          <RadioButtons>
            {sizes.map((option: ApartmentSize) => (
              <label className='radio-label' htmlFor={option.value} key={option.value}>
                <input type="radio"
                  name="apartment-size"
                  value={option.value}
                  id={option.value}
                  checked={formValues['apartment-size'] === option.value}
                  onChange={handleFormChange} />
                <div className='image-container big-icon'>
                  <img src={option.img} alt={option.label} />
                </div>
                {t(option.label, { size: option.size })}
              </label>
            ))}
          </RadioButtons>
        </WhiteContainer>
      </ ErrorWrapper>

      {/* Kulutusarvio */}
      < ErrorWrapper showError={showError && !formValues["consumption-estimate"]}>
        <WhiteContainer id='consumption' className={showError && !formValues["consumption-estimate"] ? 'error' : ''}>
          <h2>{t('phaseOne.consumptionEstimate')}</h2>
          <div>
            <label className='checkbox-label'>
              <input type="checkbox" name="electric-sauna" checked={formValues['electric-sauna'] == 'on'} onChange={handleFormChange} />
              {t('phaseOne.electricStove')}
            </label>
          </div>
          {['omakotitalo', 'mokki'].includes(formValues['apartment-type']) && <div>
            <label className='checkbox-label'>
              <input type="checkbox" name="electric-heating" checked={formValues['electric-heating'] == 'on'} onChange={handleFormChange} />
              {t('phaseOne.electricHeating')}
            </label>
          </div>}
          <div>
            <label>
              {t('phaseOne.estimatedConsumption')}
              <div className="with-suffix">
                <input type="text" name="consumption-estimate" onChange={handleFormChange} value={formValues['consumption-estimate']} />
                <span>{t('phaseOne.kwhYear')}</span>
              </div>
            </label>
          </div>
        </WhiteContainer>
      </ErrorWrapper >

      {/* Mitä toivot sähköltäsi */}
      < ErrorWrapper showError={showError && !formValues["preferences"]}>
        <WhiteContainer id='preferences' className={showError && !formValues["preferences"] ? 'error' : ''}>
          <h2>{t('phaseOne.wishForElectricity')}</h2>
          <RadioButtons>
            {preferences.map((option) => (
              <label className='radio-label' htmlFor={option.value} key={option.value}>
                <input type="radio"
                  name="preferences"
                  value={option.value}
                  id={option.value}
                  checked={formValues['preferences'] === option.value}
                  onChange={handleFormChange} />
                <div className='image-container'>
                  <img src={option.img} alt={option.label} />
                </div>
                {t(option.label)}
              </label>
            ))}
          </RadioButtons>
        </WhiteContainer>
      </ErrorWrapper >

      <ContentContainer>
        <p className={'center' + (showError ? ' error' : '')}>{t('phaseOne.continueInfo')}</p>
      </ContentContainer>

      <Actions id="next">
        <button
          type="button"
          className={"primary purple" + (Object.keys(formErrors).length !== 0 ? ' disabled' : '')}
          onClick={toNextPhase}>
          {t('common.next')}
        </button>
      </Actions>
    </>
  );
}
export default PhaseOne;
